import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const CART_KEY = "clae_cart_v1";

export function AppProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem("clae_admin_email") || null);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const addToCart = (treatment, extras = {}) => {
    setCart((c) => {
      const existing = c.find((i) => i.treatment_id === treatment.id);
      if (existing && treatment.allow_quantity) {
        return c.map((i) => i.treatment_id === treatment.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      if (existing) return c;
      return [...c, {
        treatment_id: treatment.id, name: treatment.name, price: treatment.price,
        duration_min: treatment.duration_min, deposit_type: treatment.deposit_type,
        deposit_value: treatment.deposit_value, allow_quantity: !!treatment.allow_quantity,
        category: treatment.category,
        // Consultation-only metadata (undefined for standard treatments)
        consultation_target_key: extras.consultation_target_key,
        consultation_target_label: extras.consultation_target_label,
        quantity: 1,
      }];
    });
  };
  const removeFromCart = (id) => setCart((c) => c.filter((i) => i.treatment_id !== id));
  const updateQty = (id, qty) => setCart((c) => c.map((i) => i.treatment_id === id ? { ...i, quantity: Math.max(1, qty) } : i));
  const clearCart = () => setCart([]);

  /**
   * refreshCart — re-syncs each cart line with the live treatment record so
   * duration/price/deposit changes made in the admin dashboard immediately
   * flow through to the client-facing booking flow. Treatments that were
   * deleted or deactivated are dropped from the cart.
   */
  const refreshCart = useCallback(async () => {
    if (cart.length === 0) return;
    const results = await Promise.all(
      cart.map((i) =>
        api.get(`/treatments/${i.treatment_id}`).then((r) => r.data).catch(() => null)
      )
    );
    setCart((c) =>
      c.flatMap((i, idx) => {
        const t = results[idx];
        if (!t || t.active === false) return [];
        return [{
          ...i,
          name: t.name,
          price: t.price,
          duration_min: t.duration_min,
          deposit_type: t.deposit_type,
          deposit_value: t.deposit_value,
          allow_quantity: !!t.allow_quantity,
          category: t.category,
        }];
      })
    );
  }, [cart]);

  const cartCount = cart.reduce((n, i) => n + i.quantity, 0);
  const subtotal = cart.reduce((n, i) => n + i.price * i.quantity, 0);
  const depositTotal = cart.reduce((n, i) => {
    if (i.deposit_type === "none") return n;
    if (i.deposit_type === "fixed") return n + i.deposit_value * i.quantity;
    if (i.deposit_type === "percent") return n + (i.price * i.quantity) * (i.deposit_value / 100);
    if (i.deposit_type === "full") return n + i.price * i.quantity;
    return n;
  }, 0);
  const remaining = Math.max(0, subtotal - depositTotal);
  const totalDuration = cart.reduce((n, i) => n + i.duration_min * i.quantity, 0);

  const value = {
    cart, addToCart, removeFromCart, updateQty, clearCart, refreshCart,
    cartCount, subtotal, depositTotal, remaining, totalDuration,
    adminEmail, setAdminEmail,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
